import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { undoToast } from "@/lib/undo";
import { MessageSquarePlus, FileWarning, Trash2, BookOpen, Undo2, FileQuestion } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PdfPanel } from "@/components/PdfPanel";
import { SupersedeQueue } from "@/components/SupersedeQueue";
import { RfiPackageDialog } from "@/components/RfiPackageDialog";
import { STATUS_ORDER, STATUS_LABEL, StatusPill, StatusMeta, RfiResponseFlag, RfiLink, ReopenButton, StatusSummary, GroupHeader } from "@/components/findings/StatusParts";

const SEV_OPT = ["", "critical", "high", "medium", "low"];
const ST_OPT = ["", "active", "open", "in_progress", "resolved", "dismissed"];

const COST_TONE = { increase: "text-red-400", decrease: "text-emerald-400" };
const EVIDENCE_LABEL = { inferred: "AI inference", explicit: "Explicit documented change" };
const evidenceText = (f) => `${EVIDENCE_LABEL[f.evidence] || "—"}${f.confidence ? ` · ${f.confidence} confidence` : ""}${f.impact_areas?.length ? ` · ${f.impact_areas.join(", ")}` : ""}`;

export default function Findings() {
  const { id } = useParams();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [sev, setSev] = useState("");
  const [st, setSt] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [rfiFor, setRfiFor] = useState(null);
  const [rfi, setRfi] = useState({ subject: "", body: "" });
  const [docs, setDocs] = useState([]);
  const [viewer, setViewer] = useState(null); // {docId, page}
  const [selected, setSelected] = useState([]);
  const [rfiPkgOpen, setRfiPkgOpen] = useState(false);
  const toggleSel = (fid) => setSelected((s) => (s.includes(fid) ? s.filter((x) => x !== fid) : [...s, fid]));
  const [alerts, setAlerts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [collapsed, setCollapsed] = useState({ resolved: false, dismissed: true });
  const loadAlerts = () => api.get(`/projects/${id}/rfi-alerts`).then((r) => setAlerts(r.data)).catch(() => {});
  const loadSummary = useCallback(() => api.get(`/projects/${id}/findings/summary`).then((r) => setSummary(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadAlerts(); }, [id]); // eslint-disable-line

  useEffect(() => { api.get(`/projects/${id}/documents`).then((r) => setDocs(r.data)); }, [id]);

  const jumpTo = (f) => {
    const docId = f.document_id || docs.find((d) => d.has_file)?.id || docs[0]?.id;
    if (!docId) return toast.error("No source document available");
    setViewer({ docId, page: Number(f.source_page) || 1, highlight: f.relevant_language || "" });
  };
  const viewerDoc = viewer ? docs.find((d) => d.id === viewer.docId) : null;

  const load = useCallback(async () => {
    const params = {};
    if (sev) params.severity = sev;
    if (st && st !== "active") params.status_f = st;
    const { data } = await api.get(`/projects/${id}/findings`, { params });
    setItems(st === "active" ? data.filter((f) => f.status === "open" || f.status === "in_progress") : data);
    loadSummary();
  }, [id, sev, st, loadSummary]);
  useEffect(() => { load(); }, [load]);
  const statusOptions = useMemo(() => ST_OPT.filter((s) => s && s !== "active"), []);
  const groups = useMemo(() => STATUS_ORDER.map((s) => [s, items.filter((f) => f.status === s)]).filter(([, g]) => g.length), [items]);

  const setStatus = async (fid, status, opts = {}) => {
    const f = items.find((x) => x.id === fid);
    const prev = f?.status;
    await api.patch(`/findings/${fid}`, { status });
    const label = STATUS_LABEL[status] || status;
    if ((status === "resolved" || status === "dismissed") && prev && prev !== status && !opts.silent) {
      undoToast(`${label} — ${f.title}`, null, () => load(), { onUndo: () => api.patch(`/findings/${fid}`, { status: prev }).then(load) });
    } else {
      toast.success(status === "open" ? "Reopened — back in the active workflow" : `Marked ${label}`);
    }
    load();
  };
  const reopenStatus = (f) => setStatus(f.id, "open");
  const reopen = async (f) => {
    try { const { data } = await api.post(`/findings/${f.id}/reopen`); toast.success(`Reopened as "${data.status.replace(/_/g, " ")}" — addendum link removed`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reopen failed"); }
  };

  const removeFinding = async (f) => {
    try {
      const { data } = await api.delete(`/findings/${f.id}`);
      undoToast(`Finding deleted — ${f.title}`, data.undo_id, () => load());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const openRfi = (f) => {
    setRfiFor(f);
    setRfi({ subject: `RFI: ${f.title}`, body: f.draft_rfi || `Please clarify: ${f.title}\n\nContext: ${f.relevant_language}\n\nProposed resolution: ${f.recommended_action}` });
  };

  const submitRfi = async () => {
    try {
      await api.post(`/findings/${rfiFor.id}/rfi`, { finding_id: rfiFor.id, ...rfi });
      toast.success("RFI drafted");
      setRfiFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="findings-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-cap text-amber-500">// FINDINGS &amp; RFIs</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1">Findings Management</h1>
          <p className="text-slate-400 text-sm mt-1">Severity, source citation, fabricator impact, actions, and 1-click RFI drafts.</p>
        </div>
        <div className="flex gap-2 items-end">
          <button onClick={() => setRfiPkgOpen(true)} className={`px-3 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 ${selected.length ? "bg-amber-500 hover:bg-amber-400 text-slate-950" : "border border-slate-700 text-slate-300 hover:border-amber-500"}`} title="Combine the selected findings' suggested RFIs into one numbered RFI package" data-testid="rfi-package-button"><FileQuestion className="w-3.5 h-3.5" /> RFI package{selected.length ? ` (${selected.length})` : "s"}</button>
          <label className="text-xs">
            <div className="label-cap mb-1">Severity</div>
            <select value={sev} onChange={(e) => setSev(e.target.value)}
              className="bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-100"
              data-testid="findings-severity-filter">
              {SEV_OPT.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
            </select>
          </label>
          <label className="text-xs">
            <div className="label-cap mb-1">Status</div>
            <select value={st} onChange={(e) => setSt(e.target.value)}
              className="bg-slate-950/60 border border-slate-800 rounded-md px-2 py-1.5 text-xs text-slate-100"
              data-testid="findings-status-filter">
              {ST_OPT.map((s) => <option key={s} value={s}>{s === "" ? "All" : s === "active" ? "Active (Open + In Progress)" : STATUS_LABEL[s]}</option>)}
            </select>
          </label>
        </div>
      </div>
      <div className="mb-3"><StatusSummary summary={summary} active={st} onPick={setSt} /></div>

      <div className={`grid gap-4 ${viewer ? "grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(420px,45%)]" : "grid-cols-1"}`}>
      <div className="min-w-0">
      <SupersedeQueue projectId={id} canEdit={can("edit")} onChanged={load} />
      {alerts?.overdue?.length > 0 && (
        <div className="mb-3 border border-red-500/40 bg-red-500/5 rounded-md px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="rfi-overdue-banner">
          <span className="text-red-300 font-semibold">{alerts.overdue.length} RFI package{alerts.overdue.length > 1 ? "s" : ""} past the requested response date:</span>
          {alerts.overdue.map((o) => <span key={o.id} className="font-mono text-red-200">{o.number} · {o.open_items}/{o.total} open · {o.days_overdue}d late</span>)}
          <button onClick={() => setRfiPkgOpen(true)} className="ml-auto border border-red-500/60 hover:bg-red-500/10 text-red-200 rounded px-2 py-0.5" data-testid="rfi-overdue-open-button">Open &amp; nudge</button>
        </div>
      )}
      <div className="sc-card overflow-hidden min-w-0">
        <table className="sc-table w-full">
          <thead>
            <tr>
              <th className="w-8"><input type="checkbox" checked={items.some((f) => !f.rfi_item) && selected.length === items.filter((f) => !f.rfi_item).length} onChange={() => setSelected(selected.length === items.filter((f) => !f.rfi_item).length ? [] : items.filter((f) => !f.rfi_item).map((f) => f.id))} className="accent-amber-500" title="Select all findings not yet in an RFI package" data-testid="select-all-findings" /></th><th>Sev</th><th>Title</th><th>CSI / Section</th><th>Page</th><th>Status</th><th>Mode</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([status, group]) => (
              <Fragment key={status}>
                {(status !== "open" || groups.length > 1) && <GroupHeader status={status} count={group.length} collapsed={!!collapsed[status]} onToggle={() => setCollapsed((c) => ({ ...c, [status]: !c[status] }))} />}
                {!collapsed[status] && group.map((f) => (
              <Fragment key={f.id}>
                <tr data-testid={`findings-table-row-${f.id}`} onClick={() => setExpanded(expanded === f.id ? null : f.id)} className={`cursor-pointer ${f.status === "dismissed" ? "opacity-40 grayscale" : f.status === "resolved" ? "opacity-70" : ""}`}>
                  <td onClick={(e) => e.stopPropagation()}>{f.rfi_item ? <span className="inline-flex items-center rounded border border-slate-700 bg-slate-800/60 text-slate-400 font-mono text-[10px] px-1 py-0.5 whitespace-nowrap" title={`Already included in RFI package ${f.rfi_item.number} (${f.rfi_item.status}) — cannot be selected again`} data-testid={`finding-in-rfi-${f.id}`}>{f.rfi_item.rfi_no}</span> : <input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggleSel(f.id)} className="accent-amber-500" title="Include in RFI package" data-testid={`select-finding-${f.id}`} />}</td>
                  <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold sev-${f.severity}`}>{f.severity?.toUpperCase()}</span></td>
                  <td className="text-slate-100 font-semibold">{f.title}</td>
                  <td className="font-mono text-xs">{f.csi_division} <span className="text-slate-600">·</span> {f.source_section}{f.drawing_ref && <div className="text-sky-400 mt-0.5" data-testid={`finding-drawing-ref-${f.id}`}>⌗ {f.drawing_ref}</div>}{f.mode === "addendum_review" && <div className={`mt-0.5 ${COST_TONE[f.cost_impact] || "text-slate-400"}`} data-testid={`finding-change-${f.id}`}>Δ {String(f.change_type || "").replace(/_/g, " ")} · cost {f.cost_impact || "?"}{f.schedule_impact ? " · schedule" : ""}</div>}</td>
                  <td className="font-mono" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => jumpTo(f)} className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 underline decoration-dotted underline-offset-2" title="Open source page" data-testid={`finding-page-link-${f.id}`}>
                      <BookOpen className="w-3 h-3" /> p.{f.source_page}
                    </button>
                  </td>
                  <td className="font-mono text-xs" onClick={(e) => e.stopPropagation()}><StatusPill f={f} /><StatusMeta f={f} /><div className="mt-0.5"><RfiLink f={f} onOpen={() => setRfiPkgOpen(true)} /></div><RfiResponseFlag f={f} canEdit={can("edit")} onResolve={(x) => setStatus(x.id, "resolved")} />{f.resolution === "superseded" && <div className="normal-case text-amber-400 mt-0.5 flex items-center gap-2" title={f.resolution_note} data-testid={`finding-superseded-${f.id}`}>⇄ superseded by addendum{can("edit") && <button onClick={(e) => { e.stopPropagation(); reopen(f); }} className="inline-flex items-center gap-1 text-[10px] border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 rounded px-1.5 py-0.5 text-amber-300" title="Wrong match? Reopen this finding and unlink the addendum item" data-testid={`reopen-supersede-finding-${f.id}`}><Undo2 className="w-3 h-3" /> Reopen</button>}</div>}{f.supersedes_finding_id && <div className="normal-case text-emerald-400 mt-0.5" data-testid={`finding-supersedes-${f.id}`}>✓ resolved a prior finding</div>}</td>
                  <td className="font-mono text-xs text-slate-500 capitalize">{f.mode?.replace(/_/g, " ")}</td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    {can("edit") && (
                      <>
                        {(f.status === "resolved" || f.status === "dismissed") ? <span className="mr-2"><ReopenButton f={f} onReopen={reopenStatus} /></span> : (
                        <select value={f.status} onChange={(e) => setStatus(f.id, e.target.value)}
                          className="bg-slate-950/60 border border-slate-800 rounded px-1.5 py-0.5 text-[11px] mr-2"
                          data-testid={`finding-status-select-${f.id}`}>
                          {statusOptions.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                        </select>)}
                        <button onClick={() => openRfi(f)} disabled={!!f.rfi_item} title={f.rfi_item ? `Already in ${f.rfi_item.rfi_no}` : "Draft a single RFI"} className="text-amber-400 hover:text-amber-300 disabled:text-slate-600 disabled:cursor-not-allowed text-xs inline-flex items-center gap-1 mr-2" data-testid={`create-rfi-button-${f.id}`}>
                          <MessageSquarePlus className="w-3.5 h-3.5" /> RFI
                        </button>
                        <button onClick={() => removeFinding(f)} className="text-slate-500 hover:text-red-400 inline-flex items-center" data-testid={`delete-finding-button-${f.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {expanded === f.id && (
                  <tr>
                    <td colSpan={8} className="bg-slate-950/50 border-t border-slate-800">
                      <div className="p-4 space-y-3">
                        {f.mode === "addendum_review" && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid={`finding-impact-${f.id}`}>
                            <Section label="Change" body={`${String(f.change_type || "").replace(/_/g, " ").toUpperCase() || "—"} · cost ${f.cost_impact || "?"}${f.schedule_impact ? " · schedule" : ""}`} />
                            <Section label="Previous requirement / source" body={f.previous_source || "— (new)"} />
                            <Section label="New requirement / source" body={f.new_source || `p.${f.source_page}${f.source_section ? ` · ${f.source_section}` : ""}`} />
                            <Section label="Evidence" body={evidenceText(f)} />
                          </div>
                        )}
                        <Section label="Relevant Language" body={f.relevant_language} mono />
                        <Section label="Why it matters" body={f.why_it_matters} />
                        <Section label="Recommended Action" body={f.recommended_action} />
                        {f.draft_rfi && <Section label="Draft RFI" body={f.draft_rfi} mono />}
                        {f.resolution_note && <Section label="Resolution" body={f.resolution_note} />}
                        {f.supersedes_finding && <Section label="Supersedes prior finding" body={f.supersedes_finding + (f.supersedes_finding_id ? " — earlier finding auto-marked resolved" : " — no matching open finding found")} />}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
                ))}
              </Fragment>
            ))}
            {!items.length && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-500">
                <FileWarning className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                No findings. Run an AI review from the project page.
              </td></tr>
            )}
          </tbody>
      <RfiPackageDialog projectId={id} selected={selected} findings={items} open={rfiPkgOpen} onOpenChange={setRfiPkgOpen} canEdit={can("edit")} onCreated={() => { setSelected([]); load(); loadAlerts(); }} />

        </table>
      </div>
      </div>
      {viewer && <PdfPanel docId={viewer.docId} docName={viewerDoc?.name || "Source document"} page={viewer.page} pageCount={viewerDoc?.page_count} highlight={viewer.highlight} onClose={() => setViewer(null)} />}
      </div>

      <Dialog open={!!rfiFor} onOpenChange={(o) => !o && setRfiFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-xl">
          <DialogHeader><DialogTitle className="font-heading">Draft RFI</DialogTitle></DialogHeader>
          <label className="block">
            <span className="label-cap">Subject</span>
            <input value={rfi.subject} onChange={(e) => setRfi({ ...rfi, subject: e.target.value })}
              className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100"
              data-testid="rfi-subject-input" />
          </label>
          <label className="block">
            <span className="label-cap">Body</span>
            <textarea value={rfi.body} onChange={(e) => setRfi({ ...rfi, body: e.target.value })} rows={8}
              className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2 text-slate-100 font-mono text-xs"
              data-testid="rfi-body-input" />
          </label>
          <DialogFooter>
            <button onClick={() => setRfiFor(null)} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
            <button onClick={submitRfi} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-md text-sm" data-testid="rfi-submit-button">Save RFI</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ label, body, mono }) {
  return (
    <div>
      <div className="label-cap mb-1">{label}</div>
      <div className={`text-sm text-slate-300 leading-relaxed ${mono ? "font-mono text-xs" : ""}`}>{body || "—"}</div>
    </div>
  );
}
